<template>
  <cv-multi-select
    ref="containerEl"
    v-bind="attrs"
    :options="dataOptions"
    :class="cssClasses"
    :model-value="value"
    @change="handleChange"
    @filter="handleFilter"
  >
    <template v-for="slot in Object.keys(slots)" #[slot]="slotData">
      <slot :name="slot" v-bind="slotData || {}" />
    </template>
  </cv-multi-select>
</template>

<script setup lang="ts">
import { isEmpty } from "@utils/var.check";
import DOMUtils from "@utils/dom.utils";
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useAttrs,
  useSlots,
  useTemplateRef,
  watch,
} from "vue";
import { useVm } from "@utils/vm.ts";
import { CvMultiSelectWithButton } from "@plugins/common/types.ts";
import DropDownMixin from "../mixin/dropdown.mixin";

defineOptions({
  mixins: [DropDownMixin],
});

// Props
interface Props {
  hideClearButton?: boolean;
  kind?: string;
  value?: unknown[];
  options: unknown[];
}

const { hideClearButton = false, kind = "gray", value = [], options } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  change: [data: unknown];
}>();

// Composables
const { vm } = useVm();
const slots = useSlots();
const attrs = useAttrs();

// Data
const dataOptions = ref(options);
const buttonElement = ref<HTMLButtonElement | null>(null);

// Template refs
const containerEl = useTemplateRef<CvMultiSelectWithButton>("containerEl");

// Computed
const cssClasses = computed(() => {
  return {
    [`multi-select-${kind}`]: true,
    "hide-clear-button": hideClearButton,
  };
});

// Methods
const handleChange = (newValue) => {
  emit("change", newValue);
};

const handleFilter = (filter) => {
  // to filter out unselectable header options
  if (!isEmpty(filter)) {
    dataOptions.value = options.filter((option) => !option.header);
  } else {
    dataOptions.value = options;
  }
};

const handleKeyDown = (e) => {
  // prevent `Enter` key from selecting empty item
  if (e.code === "Enter") {
    const dropdownExpanded = buttonElement.value?.getAttribute("aria-expanded") === "true";
    const highlightedMenuItem = DOMUtils.elOf(vm.$el, ".bx--list-box__menu-item--highlighted");

    if (dropdownExpanded && isEmpty(highlightedMenuItem)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
};

const handleKeyUp = (e) => {
  // prevent `Space` key from expanding and closing dropdown
  if (e.code === "Space") {
    e.preventDefault();
    e.stopPropagation();
  }
};

// Watch
watch(
  () => options,
  () => {
    dataOptions.value = options;
  },
);

// Lifecycle hooks
onMounted(() => {
  buttonElement.value = containerEl.value?.$refs?.elButton || null;
  buttonElement.value?.addEventListener("keydown", handleKeyDown);
  buttonElement.value?.addEventListener("keyup", handleKeyUp);
});

onUnmounted(() => {
  buttonElement.value?.removeEventListener("keydown", handleKeyDown);
  buttonElement.value?.removeEventListener("keyup", handleKeyUp);
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.bx--multi-select__wrapper {
  .bx--multi-select {
    &.hide-clear-button {
      .bx--tag--filter {
        padding: $spacing-02 $spacing-03;
        line-height: 13px; // to ensure the pill is rounded without cross icon
        min-height: unset;
        pointer-events: none;
      }

      .bx--tag__close-icon {
        display: none;
      }
    }

    &.indeterminate {
      .bx--list-box__menu {
        .bx--list-box__menu-item {
          &:first-of-type {
            .bx--checkbox-wrapper {
              .bx--checkbox-label {
                &::before {
                  background-color: $interactive-01;
                  border-color: $interactive-01;
                }

                // Override generic styling for the tick to make it a dash for indeterminate state
                &::after {
                  display: inline-block;
                  height: 2px;
                  width: 12px;
                  left: 0;
                  top: 0;
                  background-color: $ui-01;
                  border-radius: 20px;
                  border: none;
                  transform: translate(4px, 12px);
                }
              }
            }
          }
        }
      }
    }
  }

  .bx--tag--filter {
    margin: 0 $spacing-04 0 0;
    padding: 0 $spacing-03;
    border-radius: 12px;

    &:has(.bx--tag__close-icon) {
      padding-right: $spacing-02;
    }

    &.bx--tag--high-contrast {
      @include carbon--type-style("helper-text-01");
      background-color: $ui-05;
      color: $text-04;

      .bx--tag__close-icon {
        &:hover {
          background-color: inherit;
        }
      }
    }

    .bx--tag__label {
      padding-bottom: $spacing-01;
    }
  }

  .bx--multi-select {
    border-radius: 10px;
    border: 1px solid $ui-03;

    &.multi-select-gray {
      background-color: $ui-background;

      &:hover {
        background-color: $hover-ui;

        .bx--list-box,
        .bx--text-input {
          background-color: $hover-ui;
        }
      }

      .bx--list-box,
      .bx--text-input {
        background-color: $ui-background;
      }
    }

    &.multi-select-white {
      background-color: $ui-01;

      &:hover {
        background-color: $hover-ui;

        .bx--list-box,
        .bx--text-input {
          background-color: $hover-ui;
        }
      }

      .bx--list-box,
      .bx--text-input {
        background-color: $ui-01;
      }
    }

    &.bx--list-box[data-invalid] {
      outline: none;
      border-color: $support-01;

      .bx--text-input {
        outline: none;

        &:not(:focus) {
          outline: none;
        }
      }

      .bx--list-box__invalid-icon {
        right: 3rem;
      }

      ~ .bx--form-requirement {
        @include carbon--type-style("helper-text-01", true);
        display: flex;
        align-items: center;

        &::before {
          content: "";
          display: inline-block;
          width: 16px;
          height: 16px;
          margin-right: $spacing-02;
          padding-right: $spacing-05;
          background: url("/src/assets/images/icons/indicative/16px/Error16.svg") no-repeat;
        }
      }
    }

    &.bx--list-box--disabled {
      background-color: $disabled-01;

      &:hover {
        background-color: $disabled-01;
      }

      > .bx--list-box__field {
        pointer-events: none;
      }

      .bx--text-input {
        background-color: $disabled-01;
        pointer-events: none;
      }
    }

    &.bx--list-box--expanded {
      &:hover {
        background-color: $hover-ui;
      }
    }

    > .bx--list-box__field {
      border-radius: 10px;
      border: 1px solid transparent;

      &:focus {
        outline: none;
        border-color: $focus;
      }

      .bx--list-box__label {
        @include carbon--type-style("body-short-01", true);
      }
    }

    .bx--list-box__menu {
      border-radius: 10px;
      box-shadow: 0 2px 6px #0003;
      cursor: default;

      > .bx--list-box__menu-item {
        @include carbon--type-style("body-short-01", true);

        &.box__menu-header {
          pointer-events: none;

          .bx--list-box__menu-item__option {
            pointer-events: none;
          }

          .bx--checkbox-label {
            display: inline-flex;
            align-items: center;
            color: $text-03;
            text-transform: uppercase;
            padding-left: 0;

            &::before {
              display: none;
            }
          }

          .bx--checkbox-label-text {
            @include carbon--type-style("label-03", true);
          }
        }

        &:has(.bx--checkbox[disabled]) {
          opacity: 0.5;
          cursor: not-allowed;
          background-color: $ui-01;

          &:active,
          &:focus {
            pointer-events: none;
          }

          + .bx--list-box__menu-item {
            .bx--list-box__menu-item__option {
              border-top-color: $ui-03;
            }
          }

          .bx--list-box__menu-item__option {
            color: $text-05;
            border-top-color: $ui-03;
          }

          .bx--checkbox-wrapper {
            .bx--checkbox-label {
              &::before {
                background-color: $disabled-02;
                border-color: $disabled-02;
              }
            }
          }
        }
      }

      // Override Carbon checkbox styling
      .bx--checkbox-wrapper {
        .bx--checkbox {
          &[checked] {
            ~ .bx--checkbox-label {
              &::after {
                content: "";
                display: inline-block;
                transform: rotate(40deg) translateZ(0);
                left: 2px;
                top: 8px;
                height: 12px;
                width: 6px;
                border-bottom: 2px solid $ui-01;
                border-right: 2px solid $ui-01;
                border-left: none;
              }
            }
          }
        }

        .bx--checkbox-label {
          @include carbon--type-style("body-short-01", true);
          padding-top: 2px;

          &::after {
            display: none;
          }

          &::before {
            width: 20px;
            height: 20px;
            top: 2px;
            margin: 0;
            transform: translateZ(0);
          }
        }
      }
    }

    .bx--list-box__menu-icon {
      right: 0.5rem;
      width: 2rem;
      height: 2rem;
      padding-left: $spacing-03;
      border-left: 1px solid $ui-02;

      &.bx--list-box__menu-icon--open {
        padding: 0 $spacing-03 0 0;
        border-left: none;
        border-right: 1px solid $ui-02;
      }

      svg {
        width: 24px;
        height: 24px;
      }
    }

    .bx--list-box__label {
      @include carbon--type-style("body-short-01", true);
      text-align: left;
    }

    .bx--text-input {
      // Remove border for search input for multi-select
      border: none;

      &:focus {
        border: none;
      }
    }
  }
}

.bx--multi-select--filterable {
  .bx--list-box__selection--multi.bx--tag {
    position: static;
    margin: 0 $spacing-05;
    padding: $spacing-02 $spacing-03;

    .bx--tag__label {
      overflow: visible;
    }
  }

  .bx--list-box__selection {
    right: 3.5rem;
    width: 1rem;
    height: 1rem;
    background-color: $icon-02;
    border-radius: 100%;

    &:focus:hover {
      outline: none;
    }

    > svg {
      fill: $icon-03;
    }
  }

  .bx--multi-select--selected {
    .bx--text-input {
      padding-left: 0;
    }
  }
}
</style>
